import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Icon, Text } from '@nimbus-ds/components';
import { fetchWithCache } from '../../utils/cache.utils';
import { useNavigate } from 'react-router-dom';
import { LockIcon } from '@nimbus-ds/icons';
import { useApiClients } from '../../context';

type BillingStatus = {
  active: boolean;
  status: string; // 'active' | 'trialing' | 'canceled' | 'past_due' | 'incomplete' | 'unpaid'
  currentPeriodEnd: string | null;
};

type StoreStatus = {
  is_demo: boolean;
  billing_status: 'trial' | 'active' | 'cancelled' | 'incomplete';
  trial_started_at?: string | null;
  trial_ends_at?: string | null;
};

const TrialBar = ({ showCanceled = true }: { showCanceled?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { HuappsApi } = useApiClients();
  const [statusInfo, setStatusInfo] = useState<BillingStatus | null>(null);
  const [storeStatus, setStoreStatus] = useState<StoreStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await fetchWithCache<BillingStatus>(
        `rc:trial-bar:billing-subscription`,
        async () => {
          const { data } = await HuappsApi.get<BillingStatus>(
            `/billing/subscription`,
          );
          return data as BillingStatus;
        },
        5 * 60 * 1000,
      );
      setStatusInfo(data);
      const st = await fetchWithCache<StoreStatus>(
        `rc:trial-bar:store-status`,
        async () => {
          const { data } = await HuappsApi.get<StoreStatus>(`/store/status`);
          return data as StoreStatus;
        },
        5 * 60 * 1000,
      );
      setStoreStatus(st);
      setLoading(false);
    };
    load();
  }, []);

  const { status, remainingDays, trialActive } = useMemo(() => {
    const now = new Date();

    // Preferimos status de trial da loja (storeApp) se válido
    const trialEnds = storeStatus?.trial_ends_at
      ? new Date(storeStatus.trial_ends_at)
      : null;
    const isStoreTrialActive =
      storeStatus?.billing_status === 'trial' && trialEnds && trialEnds > now;

    let remaining = 0;
    if (isStoreTrialActive && trialEnds) {
      const diffMs = trialEnds.getTime() - now.getTime();
      remaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    } else if (
      statusInfo?.status === 'trialing' &&
      statusInfo.currentPeriodEnd
    ) {
      const endsAt = new Date(statusInfo.currentPeriodEnd);
      if (endsAt > now) {
        const diffMs = endsAt.getTime() - now.getTime();
        remaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      }
    }

    return {
      status: statusInfo?.status,
      remainingDays: remaining,
      trialActive: isStoreTrialActive || statusInfo?.status === 'trialing',
    };
  }, [statusInfo, storeStatus]);

  if (loading || !statusInfo) return null;

  if (storeStatus?.is_demo) {
    return (
      <Box marginBottom="4">
        <Alert
          appearance="neutral"
          title={t('TrialBarComponent.DemoStoreTitle') || ''}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            gap="3"
            alignItems="center"
            flexWrap="wrap"
          >
            <Text>{t('TrialBarComponent.DemoStoreMessage')}</Text>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Se trial está ativo, priorizamos a barra de trial
  if (trialActive) {
    const isEndingSoon = remainingDays <= 2;
    return (
      <Box marginBottom="4">
        <Alert
          appearance={isEndingSoon ? 'warning' : 'neutral'}
          title={
            t('TrialBarComponent.TrialTitle', { days: remainingDays }) || ''
          }
        >
          <Box
            display="flex"
            justifyContent="space-between"
            gap="3"
            alignItems="center"
            flexWrap="wrap"
          >
            <Text>
              {isEndingSoon
                ? t('TrialBarComponent.TrialEndingSoon')
                : t('TrialBarComponent.TrialActive')}
            </Text>
            <Button
              appearance="neutral"
              onClick={() => {
                navigate('/profile');
              }}
            >
              <Icon source={<LockIcon />} color="currentColor" />
              {t('TrialBarComponent.TrialButton')}
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  if (
    (status === 'canceled' || status === 'incomplete' || status === 'unpaid') &&
    showCanceled
  ) {
    return (
      <Box marginBottom="4">
        <Alert
          appearance="danger"
          title={t('TrialBarComponent.InactiveTitle') || ''}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            gap="3"
            alignItems="center"
            flexWrap="wrap"
          >
            <Text>{t('TrialBarComponent.InactiveReason')}</Text>
            <Button
              appearance="neutral"
              onClick={() => {
                navigate('/profile');
              }}
            >
              <Icon source={<LockIcon />} color="currentColor" />
              {t('TrialBarComponent.InactiveButton')}
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  // status active ou sem condição => sem barra

  return null;
};

export default TrialBar;
